import React from 'react';
import { ChannelMultiSelect } from './ChannelMultiSelect';
import { SectionCard } from './SectionCard';
import { FunnelMultiSelect } from './FunnelMultiSelect';
import { ChannelSingleSelect } from './ChannelSingleSelect';
import type { CreateAIAgentRequest } from '../services/agentsApi';

interface TriggersTabProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
}

export const TriggersTab: React.FC<TriggersTabProps> = ({ formData, setFormData }) => {
  return (
    <div className="space-y-6 max-w-3xl">
      <SectionCard icon="zap" title="Gatilho: Mensagem Recebida" subtitle="Aciona o agente quando o contato envia mensagem">
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={formData.triggers?.message_received?.enabled || false}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  triggers: {
                    ...formData.triggers,
                    message_received: {
                      ...formData.triggers?.message_received,
                      enabled: e.target.checked,
                    },
                  },
                })
              }
              className="w-4 h-4"
              style={{ accentColor: 'var(--theme-primary)' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Ativar agente quando mensagem for recebida
            </span>
          </label>

          {formData.triggers?.message_received?.enabled && (
            <div className="space-y-4">
              {/* Match Mode */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Modo de Acionamento
                </label>
                <select
                  value={formData.triggers?.message_received?.conditions?.matchMode || 'any_message'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        message_received: {
                          ...formData.triggers?.message_received,
                          enabled: true,
                          conditions: {
                            ...formData.triggers?.message_received?.conditions,
                            matchMode: e.target.value as any,
                          },
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                  style={{
                    backgroundColor: 'var(--theme-input-background)',
                    borderColor: 'var(--theme-input-border)',
                    color: 'var(--theme-input-text)',
                  }}
                >
                  <option value="any_message">Qualquer mensagem</option>
                  <option value="contains">Mensagem contém palavra</option>
                  <option value="starts_with">Mensagem inicia com palavra</option>
                  <option value="ends_with">Mensagem termina com palavra</option>
                  <option value="exact_match">Correspondência exata</option>
                </select>
              </div>

              {/* Keywords */}
              {formData.triggers?.message_received?.conditions?.matchMode !== 'any_message' && (
                <>
                  <div>
                    <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                      Palavras-chave (separadas por vírgula)
                    </label>
                    <input
                      type="text"
                      placeholder="ex: ajuda, suporte, atendimento"
                      value={formData.triggers?.message_received?.conditions?.keywords?.join(', ') || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          triggers: {
                            ...formData.triggers,
                            message_received: {
                              ...formData.triggers?.message_received,
                              enabled: true,
                              conditions: {
                                ...formData.triggers?.message_received?.conditions,
                                keywords: e.target.value.split(',').map((k) => k.trim()).filter((k) => k),
                              },
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>

                  {/* Keyword Logic */}
                  {(formData.triggers?.message_received?.conditions?.keywords?.length || 0) > 1 && (
                    <div>
                      <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                        Lógica de Matching
                      </label>
                      <select
                        value={formData.triggers?.message_received?.conditions?.keywordLogic || 'OR'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            triggers: {
                              ...formData.triggers,
                              message_received: {
                                ...formData.triggers?.message_received,
                                enabled: true,
                                conditions: {
                                  ...formData.triggers?.message_received?.conditions,
                                  keywordLogic: e.target.value as 'OR' | 'AND',
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                        style={{
                          backgroundColor: 'var(--theme-input-background)',
                          borderColor: 'var(--theme-input-border)',
                          color: 'var(--theme-input-text)',
                        }}
                      >
                        <option value="OR">OU (qualquer palavra encontrada)</option>
                        <option value="AND">E (todas as palavras encontradas)</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* Canais Permitidos */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Canais Permitidos (obrigatório)
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  O gatilho só dispara em canais selecionados nesta lista.
                </p>
                {(formData.triggers?.message_received?.conditions?.channelIds?.length || 0) === 0 && (
                  <p
                    className="text-xs mb-2 font-medium"
                    style={{ color: 'var(--theme-error-color)' }}
                    data-testid="trigger-message-received-no-channel-warning"
                  >
                    Nenhum canal selecionado — o gatilho não será disparado até que ao menos um canal seja escolhido.
                  </p>
                )}
                <ChannelMultiSelect
                  selectedChannelIds={formData.triggers?.message_received?.conditions?.channelIds || []}
                  onSelectionChange={(channelIds) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        message_received: {
                          ...formData.triggers?.message_received,
                          enabled: true,
                          conditions: {
                            ...formData.triggers?.message_received?.conditions,
                            channelIds,
                          },
                        },
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
      </SectionCard>

      {/* Trigger: Mensagem Enviada */}
      <SectionCard icon="send" title="Gatilho: Mensagem Enviada" subtitle="Aciona o agente a partir de mensagens enviadas pela empresa">
          <label className="flex items-center gap-2 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={formData.triggers?.message_sent?.enabled || false}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  triggers: {
                    ...formData.triggers,
                    message_sent: {
                      ...formData.triggers?.message_sent,
                      enabled: e.target.checked,
                    },
                  },
                })
              }
              className="w-4 h-4"
              style={{ accentColor: 'var(--theme-primary)' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Ativar agente quando mensagem for enviada pelo operador
            </span>
          </label>

          {formData.triggers?.message_sent?.enabled && (
            <div className="space-y-4">
              {/* Match Mode */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Modo de Acionamento
                </label>
                <select
                  value={formData.triggers?.message_sent?.conditions?.matchMode || 'any_message'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        message_sent: {
                          ...formData.triggers?.message_sent,
                          enabled: true,
                          conditions: {
                            ...formData.triggers?.message_sent?.conditions,
                            matchMode: e.target.value as any,
                          },
                        },
                      },
                    })
                  }
                  className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                  style={{
                    backgroundColor: 'var(--theme-input-background)',
                    borderColor: 'var(--theme-input-border)',
                    color: 'var(--theme-input-text)',
                  }}
                >
                  <option value="any_message">Qualquer mensagem</option>
                  <option value="contains">Mensagem contém palavra</option>
                  <option value="starts_with">Mensagem inicia com palavra</option>
                  <option value="ends_with">Mensagem termina com palavra</option>
                  <option value="exact_match">Correspondência exata</option>
                </select>
              </div>

              {/* Keywords */}
              {formData.triggers?.message_sent?.conditions?.matchMode !== 'any_message' && (
                <>
                  <div>
                    <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                      Palavras-chave (separadas por vírgula)
                    </label>
                    <input
                      type="text"
                      placeholder="ex: ajuda, suporte, atendimento"
                      value={formData.triggers?.message_sent?.conditions?.keywords?.join(', ') || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          triggers: {
                            ...formData.triggers,
                            message_sent: {
                              ...formData.triggers?.message_sent,
                              enabled: true,
                              conditions: {
                                ...formData.triggers?.message_sent?.conditions,
                                keywords: e.target.value.split(',').map((k) => k.trim()).filter((k) => k),
                              },
                            },
                          },
                        })
                      }
                      className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        borderColor: 'var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                    />
                  </div>

                  {/* Keyword Logic */}
                  {(formData.triggers?.message_sent?.conditions?.keywords?.length || 0) > 1 && (
                    <div>
                      <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                        Lógica de Matching
                      </label>
                      <select
                        value={formData.triggers?.message_sent?.conditions?.keywordLogic || 'OR'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            triggers: {
                              ...formData.triggers,
                              message_sent: {
                                ...formData.triggers?.message_sent,
                                enabled: true,
                                conditions: {
                                  ...formData.triggers?.message_sent?.conditions,
                                  keywordLogic: e.target.value as 'OR' | 'AND',
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 rounded-lg border text-sm h-[38px]"
                        style={{
                          backgroundColor: 'var(--theme-input-background)',
                          borderColor: 'var(--theme-input-border)',
                          color: 'var(--theme-input-text)',
                        }}
                      >
                        <option value="OR">OU (qualquer palavra encontrada)</option>
                        <option value="AND">E (todas as palavras encontradas)</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {/* Canais Permitidos */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Canais Permitidos (obrigatório)
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  O gatilho só dispara em canais selecionados nesta lista.
                </p>
                {(formData.triggers?.message_sent?.conditions?.channelIds?.length || 0) === 0 && (
                  <p
                    className="text-xs mb-2 font-medium"
                    style={{ color: 'var(--theme-error-color)' }}
                    data-testid="trigger-message-sent-no-channel-warning"
                  >
                    Nenhum canal selecionado — o gatilho não será disparado até que ao menos um canal seja escolhido.
                  </p>
                )}
                <ChannelMultiSelect
                  selectedChannelIds={formData.triggers?.message_sent?.conditions?.channelIds || []}
                  onSelectionChange={(channelIds) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        message_sent: {
                          ...formData.triggers?.message_sent,
                          enabled: true,
                          conditions: {
                            ...formData.triggers?.message_sent?.conditions,
                            channelIds,
                          },
                        },
                      },
                    })
                  }
                />
              </div>
            </div>
          )}
      </SectionCard>

      {/* Trigger: Lead Recebido via API */}
      <SectionCard icon="user-plus" title="Gatilho: Lead Recebido via API" subtitle="Aciona o agente quando um lead é criado via API">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.triggers?.lead_created?.enabled || false}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  triggers: {
                    ...formData.triggers,
                    lead_created: {
                      ...formData.triggers?.lead_created,
                      enabled: e.target.checked,
                    },
                  },
                })
              }
              className="w-4 h-4"
              style={{ accentColor: 'var(--theme-primary)' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Ativar agente quando lead for recebido pelo endpoint público
            </span>
          </label>
          <p className="text-xs mt-2 ml-6" style={{ color: 'var(--theme-muted-text)' }}>
            O agente será ativado automaticamente quando um lead for cadastrado via API pública (POST /leads/public/:stepId).
          </p>

          {/* Configurações expandidas quando habilitado */}
          {formData.triggers?.lead_created?.enabled && (
            <div className="mt-4 ml-6 space-y-4">
              {/* Filtro de Funis */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Funis permitidos (opcional)
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Se nenhum funil for selecionado, o gatilho funcionará para todos os funis.
                </p>
                <FunnelMultiSelect
                  selectedFunnelIds={(formData.triggers?.lead_created as any)?.funnelIds || []}
                  onSelectionChange={(funnelIds) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        lead_created: {
                          ...formData.triggers?.lead_created,
                          enabled: true,
                          funnelIds,
                        } as any,
                      },
                    })
                  }
                />
              </div>

              {/* Canal para leads com telefone */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Canal para leads com telefone
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Quando o lead tiver telefone, a conversa será criada neste canal.
                </p>
                <ChannelSingleSelect
                  selectedChannelId={(formData.triggers?.lead_created as any)?.phoneChannelId || ''}
                  onSelectionChange={(phoneChannelId) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        lead_created: {
                          ...formData.triggers?.lead_created,
                          enabled: true,
                          phoneChannelId,
                        } as any,
                      },
                    })
                  }
                  placeholder="Selecione o canal de telefone..."
                />
              </div>

              {/* Canal para leads com email */}
              <div>
                <label className="block text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Canal para leads com email (sem telefone)
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Quando o lead não tiver telefone mas tiver email, a conversa será criada neste canal.
                </p>
                <ChannelSingleSelect
                  selectedChannelId={(formData.triggers?.lead_created as any)?.emailChannelId || ''}
                  onSelectionChange={(emailChannelId) =>
                    setFormData({
                      ...formData,
                      triggers: {
                        ...formData.triggers,
                        lead_created: {
                          ...formData.triggers?.lead_created,
                          enabled: true,
                          emailChannelId,
                        } as any,
                      },
                    })
                  }
                  placeholder="Selecione o canal de email..."
                />
              </div>

              {/* Nota de prioridade */}
              <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Prioridade: Telefone → Email. Se o lead possuir telefone, o canal de telefone será utilizado. Caso contrário, se possuir email, o canal de email será usado.
              </p>
            </div>
          )}
      </SectionCard>
    </div>
  );
};
